import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "nodo-info" }
const _hoisted_2 = { class: "nodo-encabezado" }
const _hoisted_3 = { class: "nodo-numeracion" }
const _hoisted_4 = { class: "nodo-nombre" }
const _hoisted_5 = { class: "nodo-descripcion" }
const _hoisted_6 = { class: "nodo-peso" }
const _hoisted_7 = {
  key: 0,
  class: "peso-porcentaje"
}
const _hoisted_8 = { class: "peso-input-row" }
const _hoisted_9 = ["value", "disabled"]
const _hoisted_10 = {
  key: 0,
  class: "nodo-hijos"
}

import { ref, computed, inject, type Ref } from 'vue'
import type Componente from '@/interfaces/Componente'
import { sumaColapsada, colorDeGrupo } from '@/utils/pesosGrupo'

interface ComponenteAplanado {
  ref: Componente
  nivel: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NodoComponente',
  props: {
    componente: {},
    nivel: {},
    loading: { type: Boolean },
    lockedMap: {},
    componentes: {},
    storeRaizId: {},
    obtenerNumeracion: { type: Function },
    mostrarHijos: { type: Boolean },
    nivelMaximo: {}
  },
  emits: ["limitar-decimales", "redistribuir", "formatear", "update-peso"],
  setup(__props: any, { emit: __emit }) {

/**
 * Nodo RECURSIVO del árbol de estructura.
 *
 * Se invoca a sí mismo para pintar su descendencia (patrón Composite). El árbol
 * llega a 4 niveles, y la profundidad se comunica con color y sangría.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const props = __props

const emit = __emit

const tieneHijos = computed(() =>
  (props.mostrarHijos !== false) &&
  (props.nivelMaximo === undefined || props.nivel < props.nivelMaximo) &&
  ((props.componente.children?.length ?? 0) > 0)
)

const hijosOrdenados = computed(() =>
  [...(props.componente.children ?? [])].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const claseNivel = computed(() => `nodo-nivel-${Math.min(props.nivel, 5)}`)

// Color determinístico del grupo de peso compartido (diferenciador visual).
const grupoColor = computed(() =>
  props.componente.grupo_variante ? colorDeGrupo(props.componente.grupo_variante) : null
)

const aplanado = computed<ComponenteAplanado>(() => ({
  ref: props.componente,
  nivel: props.nivel
}))

const numeracionJerarquica = computed(() => {
  if (props.obtenerNumeracion) {
    return props.obtenerNumeracion(props.componente, props.nivel)
  }
  return props.componente.ordinal ?? ''
})

// Inyectado desde la vista para obtener el nombre del nivel
const obtenerNombreNivel = inject<(nivel: number) => string>('obtenerNombreNivel', () => '')
const decimales = inject<Ref<number>>('decimales')

const mensajeError = computed(() => {
  const hijos = props.componente.children ?? []
  if (!hijos.length) return null
  const dec = decimales?.value ?? 2
  // Suma colapsando grupos de peso compartido (variantes cuentan una vez).
  const suma = sumaColapsada(hijos)
  const sumaRedondeada = parseFloat(suma.toFixed(dec))
  const totalEsperado = props.componente.padre_id == null ? 100 : (props.componente.peso ?? 0)
  const diferencia = Math.abs(sumaRedondeada - totalEsperado)
  if (diferencia > 0.5) {
    return `La suma de los hijos es ${sumaRedondeada}, debe ser ${totalEsperado}.`
  }
  return null
})

const editando = ref(false)

const valorMostrado = computed(() => {
  if (editando.value || props.componente.peso == null) return props.componente.peso ?? 0
  const dec = decimales?.value ?? 2
  return Number(props.componente.peso.toFixed(dec))
})

const porcentajeTexto = computed(() => {
  const peso = props.componente.peso
  if (peso == null) return null

  const padreId = props.componente.padre_id
  const esNivel1 = padreId == null || padreId === props.storeRaizId
  let totalPadre: number

  if (esNivel1) {
    totalPadre = 100
  } else {
    const padre = props.componentes.find(c => c.ref.id_componente === padreId)?.ref
    if (!padre || !padre.peso) return null
    totalPadre = padre.peso
  }

  const pct = (peso / totalPadre) * 100
  const redondeado = parseFloat(pct.toFixed(1))

  if (!esNivel1) {
    const nombreNivelPadre = obtenerNombreNivel(props.nivel - 1)
    const num = numeracionJerarquica.value
    const numPadre = num.split('.').slice(0, -1).join('.')
    if (nombreNivelPadre && numPadre) {
      return `${redondeado}% ${nombreNivelPadre} ${numPadre}`
    }
  }

  return `${redondeado}%`
})

let pesoAlEnfocar: number | null = null

function onFocus() {
  editando.value = true
  pesoAlEnfocar = props.componente.peso ?? null
}

function onKeydown(event: KeyboardEvent) {
  if (event.ctrlKey || event.metaKey) return
  const permitidas = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End']
  if (permitidas.includes(event.key)) return
  if (event.key === ',' || event.key === '.') return
  if (event.key >= '0' && event.key <= '9') return
  event.preventDefault()
}

function onPesoInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value.replace(',', '.')
  const valor = parseFloat(raw) || 0
  emit('update-peso', { comp: props.componente, valor })
  emit('limitar-decimales', props.componente)
}

function onBlur() {
  editando.value = false
  emit('formatear', props.componente)
  if (props.componente.peso !== pesoAlEnfocar) {
    emit('redistribuir', aplanado.value)
  }
  pesoAlEnfocar = null
}

return (_ctx: any,_cache: any) => {
  const _component_NodoComponente = _resolveComponent("NodoComponente", true)!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["nodo-componente", claseNivel.value])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["nodo-cabecera", { 'nodo-error': !!mensajeError.value, 'nodo-padre': tieneHijos.value, 'nodo-agrupado': !!grupoColor.value }]),
      style: _normalizeStyle(grupoColor.value ? { borderLeftColor: grupoColor.value.borde, borderLeftWidth: '5px' } : {})
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("strong", _hoisted_3, _toDisplayString(_unref(obtenerNombreNivel)(_ctx.nivel)) + " " + _toDisplayString(numeracionJerarquica.value), 1),
          _createElementVNode("strong", _hoisted_4, _toDisplayString(_ctx.componente.nombre), 1),
          (_ctx.componente.grupo_variante && grupoColor.value)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: "grupo-chip",
                style: _normalizeStyle({ backgroundColor: grupoColor.value.bg, color: grupoColor.value.texto, borderColor: grupoColor.value.borde }),
                title: "Aspectos con este mismo grupo comparten un único slot de peso"
              }, "⧉ " + _toDisplayString(_ctx.componente.grupo_variante), 5))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.componente.descripcion || '—'), 1)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (porcentajeTexto.value != null)
          ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(porcentajeTexto.value), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_8, [
          _cache[4] || (_cache[4] = _createElementVNode("span", { class: "peso-label" }, "Peso:", -1)),
          _createElementVNode("input", {
            type: "text",
            inputmode: "decimal",
            value: valorMostrado.value,
            class: "input-personalizado",
            onFocus: onFocus,
            onKeydown: onKeydown,
            onInput: onPesoInput,
            onBlur: onBlur,
            disabled: _ctx.loading
          }, null, 40, _hoisted_9)
        ])
      ])
    ], 6),
    (tieneHijos.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hijosOrdenados.value, (hijo) => {
            return (_openBlock(), _createBlock(_component_NodoComponente, {
              key: hijo.id_componente,
              componente: hijo,
              nivel: _ctx.nivel + 1,
              loading: _ctx.loading,
              lockedMap: _ctx.lockedMap,
              componentes: _ctx.componentes,
              storeRaizId: _ctx.storeRaizId,
              obtenerNumeracion: _ctx.obtenerNumeracion,
              mostrarHijos: _ctx.mostrarHijos,
              nivelMaximo: _ctx.nivelMaximo,
              onLimitarDecimales: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('limitar-decimales', $event))),
              onRedistribuir: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('redistribuir', $event))),
              onFormatear: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('formatear', $event))),
              onUpdatePeso: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update-peso', $event)))
            }, null, 8, ["componente", "nivel", "loading", "lockedMap", "componentes", "storeRaizId", "obtenerNumeracion", "mostrarHijos", "nivelMaximo"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})